import { useEffect, useMemo, VFC } from 'react';
import { Card, message } from 'antd';
import { useQuery } from 'react-query';
import { MESSAGES_REFETCH_INTERVAL_MS } from 'consts/common';
import { captureException } from '@sentry/react';
import { useIntl } from 'react-intl';
import { ContentSensorSuccess } from './content/Success';
import { ContentSensorNotResponding } from './content/NotResponding';
import { ContentSensorNotFound } from './content/NotFound';
import { useLoraMessages } from '../../../../hooks/useLoraMessages';
import { useStore } from '../../useStore';
import { SensorLoraState } from './slice';
import { NotFoundContent } from '../../../not_found/components/NotFoundContent';
import { getLoraDevicesApi } from '../../../../api/loraDevices';
import { QUERY_KEYS } from '../../../../utils/queryKeys';
import { ContentSensorRegistration } from './content/Registration';
import { RefetchIndicator } from '../../../../components/refetch_indicator/RefetchIndicator';
import { LoadingSkeleton } from './LoadingSkeleton';
import { ContentSensorRegistration as messages } from './messages';

const loraDevicesApi = getLoraDevicesApi();

export const SensorConnectionStepContent: VFC = () => {
  const intl = useIntl();
  const { dataSourceId, sensorLoraState, setSensorLoraState } = useStore();

  const { loraMessages, loading, lastFetchTime } = useLoraMessages(dataSourceId);

  const { data: loraDevices, isFetching } = useQuery(
    QUERY_KEYS.loraDevices.find({ dev_eui: dataSourceId }),
    async () => {
      if (!dataSourceId) {
        return [];
      }

      return loraDevicesApi.findByDevEui(dataSourceId);
    },
    {
      onError: err => {
        console.error(err);
        captureException(err);
        message.error(intl.formatMessage(messages.error));
      },
      enabled: !!dataSourceId,
    },
  );

  useEffect(() => {
    if (!loraMessages) {
      if (loraDevices !== undefined && loraDevices.length > 0) {
        setSensorLoraState(SensorLoraState.NotRegistered);
      } else {
        setSensorLoraState(SensorLoraState.NotInDatabase);
      }
    } else if (loraMessages.length === 0) {
      setSensorLoraState(SensorLoraState.NotResponding);
    } else {
      setSensorLoraState(SensorLoraState.OK);
    }
  }, [setSensorLoraState, loraDevices, loraMessages]);

  const content = useMemo(() => {
    switch (sensorLoraState) {
      case SensorLoraState.OK:
        return (
          <>
            <RefetchIndicator fetchStart={lastFetchTime} refetchInterval={MESSAGES_REFETCH_INTERVAL_MS} />
            <ContentSensorSuccess messages={loraMessages ?? []} />
          </>
        );
      case SensorLoraState.NotRegistered:
        return <ContentSensorRegistration />;
      case SensorLoraState.NotResponding:
        return (
          <>
            <RefetchIndicator fetchStart={lastFetchTime} refetchInterval={MESSAGES_REFETCH_INTERVAL_MS} />
            <ContentSensorNotResponding />
          </>
        );
      case SensorLoraState.NotInDatabase:
        return <ContentSensorNotFound />;
      default:
        return <NotFoundContent />;
    }
  }, [sensorLoraState, loraMessages, lastFetchTime]);

  return (
    <Card bodyStyle={{ padding: 0 }} bordered={false}>
      {loading || isFetching ? <LoadingSkeleton /> : content}
    </Card>
  );
};
